import BackLink from '@ep/components/BackLink';
import Container from '@ep/components/Container';
import Layout from '@ep/components/Layout';
import SEO from '@ep/components/Seo';
import Gallery3DCarouselModule from '@ep/modules/Gallery3DCarouselModule';
import RelatedProductsModule from '@ep/modules/RelatedProductsModule';
import { Gcms_ProductCtaPage } from '@ep/types/gatsbyTypes';
import getModuleProps from '@ep/utils/getModuleProps';
import getSeoProps from '@ep/utils/getSeoProps';
import { WindowLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React from 'react';
import HeroCTABarModule, {
  HeroCTABarModuleProps,
} from '@ep/modules/HeroCTABarModule';
import MixedMediaModule, {
  MixedMediaProps,
} from '@ep/modules/MixedMediaModule';

import FlexBannerModule, {
  FlexBannerModuleProps,
} from '@ep/modules/FlexBannerModule';
import TestimonialModule, {
  TestimonialModuleProps,
} from '@ep/modules/TestimonialModule';

export interface ProductCtaPageProps {
  data: {
    gcms: {
      productCtaPage: Gcms_ProductCtaPage;
    };
  };
  location: WindowLocation;
}

const ProductCtaPage = ({ data, location }: ProductCtaPageProps) => {
  const pageData: Gcms_ProductCtaPage = data.gcms.productCtaPage;
  const hasGallerySection = Boolean(pageData.productGallery?.length);
  const hasHeroSection = Boolean(pageData.heroTitle && pageData.heroCtaLabel);
  const hasTestimonial = Boolean(pageData.productCtaTestimonials?.length);
  const hasMixedMediaSection = Boolean(pageData.productMainDescription);
  const hasRelatedProductsSection = Boolean(pageData.relatedProducts?.length);
  const hasBackLink = Boolean(pageData.backLinkLabel && pageData.backLinkUrl);
  return (
    <Layout pathname={location.pathname}>
      <SEO {...getSeoProps(pageData)} />
      {hasHeroSection && (
        <HeroCTABarModule
          {...(getModuleProps(pageData, 'hero') as HeroCTABarModuleProps)}
        />
      )}
      { pageData.flexBannerModule?.map((flexBanner)=>{
          if (flexBanner.flexBannerDisplay ){
            return <FlexBannerModule 
               {...(getModuleProps({...flexBanner}, 'flexBanner') as FlexBannerModuleProps)}
             />;
          }
        })
      }
      {hasBackLink && (
        <Container>
          <BackLink label={pageData.backLinkLabel} to={pageData.backLinkUrl} />
        </Container>
      )};
      {hasMixedMediaSection && (
        <MixedMediaModule
        {...(getModuleProps(pageData, 'product') as MixedMediaProps)}
        />
      )}
      {hasGallerySection && (
        <Gallery3DCarouselModule mediaGallery={pageData.productGallery} />
      )}
      {hasTestimonial && (
        <TestimonialModule
          {...(getModuleProps(
            pageData,
            'productCta',
          ) as TestimonialModuleProps)}
        />
      )}
      {hasRelatedProductsSection && (
        <RelatedProductsModule
          heading={pageData.relatedProductHeading}
          relatedProducts={pageData.relatedProducts}
        />
      )}
    </Layout>
  );
};

export const query = graphql`
  query ProductCtaPageQuery($id: ID!) {
    gcms {
      productCtaPage(where: { id: $id }) {
        heroTitle
        title: heroTitle
        description
        structuredData
        websiteType
        slug
        featuredImage {
          altText
          url
          vidyardLink
        }
        heroCtaLabel
        heroCtaLink
        heroCtaModalText {
          html
        }
        heroCtaModalButtonLabel
        heroCtaModalButtonLink
        flexBannerModule {
        ... on GCMS_FlexBannerModule {
          id
          moduleType
          scrollToLink
          flexBannerDisplay
          flexBannerBackgroundColor {
            hex
          }
          flexBannerTextColor {
            hex
          }
          flexBannerContent {
            html
          }
          flexBannerExpirationDateTime
          flexBannerShow
          }
        }
        productMainDescription {
          html
        }
        productMainMedia {
          altText
          url
          vidyardLink
        }
        productSDSBColor {
          hex
        }
        productSideDescription {
          html
        }
        productSideMediaThumbnail {
          altText
          url
          vidyardLink
        }
        productSideMedia {
          altText
          url
          vidyardLink
        }
        productGallery {
          id
          altText
          url
          vidyardLink
        }
        productCtaTestimonials {
          author
          id
          jobTitle
          productionName
          publishedAt
          quote
        }
        relatedProductHeading
        relatedProducts {
          name
          link
          image {
            url
            altText
            vidyardLink
            options
          }
        }
        backLinkLabel
        backLinkUrl
      }
    }
  }
`;

export default ProductCtaPage;
